import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../store/useAuthStore';

export default function RegisterAfterPayment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const token = searchParams.get('token') || '';
  const checkoutIntentToken = searchParams.get('intent') || '';
  const [form, setForm] = useState({
    clinicName: '',
    doctorName: '',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token && !checkoutIntentToken) {
      setError('A verified payment token is required before registration.');
    }
  }, [token, checkoutIntentToken]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!token && !checkoutIntentToken) {
      setError('A verified payment token is required before registration.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Password and confirm password must match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/subscriptions/onboarding/complete-registration', {
        paidOnboardingToken: token,
        checkoutIntentToken,
        clinicName: form.clinicName.trim(),
        doctorName: form.doctorName.trim(),
        email: form.email.trim().toLowerCase(),
        mobileNumber: form.mobileNumber.trim(),
        password: form.password,
      });
      login(data.token, data.user);
      navigate(data.redirectTo || '/setup', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to complete paid registration.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-3xl rounded-3xl bg-white p-8 shadow-xl">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-600">Payment verified</div>
        <h1 className="mt-3 text-3xl font-bold text-slate-950">Create your clinic workspace</h1>
        <p className="mt-2 text-sm text-slate-600">
          Registration is unlocked only after verified Razorpay payment. Your tenant will be created after this form is submitted.
        </p>

        {!token ? (
          <div className="mt-6 rounded-2xl bg-rose-50 p-4 text-sm text-rose-700">
            Verified payment token is missing. Please start from the subscription checkout.
            <div className="mt-3">
              <Link to="/subscribe" className="font-semibold text-rose-800 underline">Go to subscription checkout</Link>
            </div>
          </div>
        ) : null}

        <form onSubmit={submit} className="mt-6 grid gap-4">
          <input name="clinicName" value={form.clinicName} onChange={updateField} placeholder="Clinic Name" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500" required />
          <input name="doctorName" value={form.doctorName} onChange={updateField} placeholder="Lead Doctor Name" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500" required />
          <input type="email" name="email" value={form.email} onChange={updateField} placeholder="Email" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500" required />
          <input name="mobileNumber" value={form.mobileNumber} onChange={updateField} placeholder="Mobile Number" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500" required />
          <div className="grid gap-4 md:grid-cols-2">
            <input type="password" name="password" value={form.password} onChange={updateField} placeholder="Password" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500" required />
            <input type="password" name="confirmPassword" value={form.confirmPassword} onChange={updateField} placeholder="Confirm Password" className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500" required />
          </div>

          {error ? <div className="rounded-2xl bg-rose-50 p-3 text-sm text-rose-700">{error}</div> : null}

          <button type="submit" disabled={loading || (!token && !checkoutIntentToken)} className="rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white disabled:opacity-60">
            {loading ? 'Creating workspace...' : 'Create Clinic Workspace'}
          </button>
        </form>

        <div className="mt-5 text-sm text-slate-500">
          Already registered? <Link to="/login" className="font-semibold text-blue-700">Login to Software</Link>
        </div>
      </div>
    </div>
  );
}
